import { useState } from 'react';
import { User } from '../../../lib/auth-store';
import { Link } from 'react-router-dom';
import { BrandMark } from '@rynora/theme';
import { ProductDemo } from '../components/ProductDemo';
import { PaymentMethodsRow } from '../components/PaymentMethodsRow';
import { getApiBaseUrl } from '../../../lib/config';
import '../components/product-demo.css';
import {
  Sparkles,
  Shield,
  Zap,
  Eye,
  Code2,
  ArrowRight,
  Brain,
  Target,
  RefreshCw,
  Lock,
  AlertTriangle,
  Users,
  GitBranch,
  Activity,
  FileCheck,
  Server,
} from 'lucide-react';

interface LandingPageProps {
  user: User | null;
  onNavigate: (page: string) => void;
  onLogout: () => void;
}

const SPINE = [
  'Code change',
  'Change Impact',
  'TIA',
  'Targeted tests',
  'Execution',
  'Failure Intelligence',
  'Maintenance',
  'Release Confidence',
  'Evidence',
] as const;

export function LandingPage({ user, onNavigate }: LandingPageProps) {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [waitlistError, setWaitlistError] = useState('');

  const handleWaitlist = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setWaitlistError('');
    try {
      const apiBase = getApiBaseUrl();
      const res = await fetch(`${apiBase}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), source: 'landing-hero' }),
      });
      if (res.ok) {
        setSubmitted(true);
        setEmail('');
      } else {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setWaitlistError(body.error || 'Could not sign you up right now. Please try again.');
      }
    } catch {
      setSubmitted(true);
      setEmail('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <nav className="nav">
        <div className="container nav-inner">
          <Link to="/" className="nav-logo" aria-label="Rynora — home">
            <BrandMark variant="compact" />
          </Link>
          <ul className="nav-links">
            <li>
              <a href="#problems">Why Rynora</a>
            </li>
            <li>
              <a href="#spine">Quality Intelligence</a>
            </li>
            <li>
              <a href="#capabilities">Capabilities</a>
            </li>
            <li>
              <a href="#pricing">Pricing</a>
            </li>
            <li>
              <Link to="/docs">Docs</Link>
            </li>
            <li>
              <Link to="/trust">Trust</Link>
            </li>
          </ul>
          <div className="nav-auth">
            {user ? (
              <>
                <button className="nav-auth-btn" onClick={() => onNavigate('dashboard')}>
                  Dashboard
                </button>
                <button className="nav-cta" onClick={() => onNavigate('dashboard')}>
                  Open App
                </button>
              </>
            ) : (
              <>
                <button className="nav-auth-btn" onClick={() => onNavigate('login')}>
                  Sign In
                </button>
                <button className="nav-cta" onClick={() => onNavigate('register')}>
                  Get Started Free
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      <section className="hero">
        <div className="container">
          <div className="hero-badge">
            <span className="hero-badge-dot" />
            Governed Quality Intelligence for change-aware QE
          </div>
          <h1>
            Know what changed —
            <br />
            and whether you can still <span className="hero-highlight">ship</span>
          </h1>
          <p>
            Rynora is the system of record for product quality. It connects code change to Change Impact, Test Impact
            Analysis, targeted execution, Failure Intelligence, maintenance recommendations, and advisory Release
            Confidence — with human approval and exportable evidence. AI accelerates authoring and analysis;
            deterministic Rynora services drive execution, diagnosis, governance, and release evidence.
          </p>
          <div className="hero-actions">
            <button className="btn-hero btn-hero-primary" onClick={() => onNavigate('register')}>
              Get Early Access <ArrowRight size={16} style={{ marginLeft: 4 }} />
            </button>
            <a href="#spine">
              <button className="btn-hero btn-hero-secondary">See the workflow</button>
            </a>
          </div>

          <ProductDemo />

          <p className="section-subtitle" style={{ marginTop: 32, maxWidth: 720, marginInline: 'auto' }}>
            Rynora doesn&apos;t just tell you whether a test passed. It connects what changed, what should be tested,
            what failed, what needs attention, and what evidence supports the release decision.
          </p>
        </div>
      </section>

      <section className="section" id="problems">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">The Problem</div>
            <h2 className="section-title">Teams test too much, too little, and often don&apos;t know why</h2>
            <p className="section-subtitle">
              Pass/fail dashboards do not answer the release question. Change-aware Quality Engineering needs a
              connected story — not more scripts to maintain.
            </p>
          </div>

          <div className="problems-grid">
            <div className="problem-card">
              <div className="problem-icon yellow">
                <Target size={22} />
              </div>
              <h3>Wrong tests for the change</h3>
              <p>
                Full suites burn CI time while the risky paths go untested. Without Test Impact Analysis and Change
                Impact, selection is guesswork.
              </p>
            </div>
            <div className="problem-card">
              <div className="problem-icon red">
                <AlertTriangle size={22} />
              </div>
              <h3>Failures without diagnosis</h3>
              <p>
                A red run is not a decision. Teams need Failure Intelligence, similar history, and resolution evidence —
                not another raw stack trace.
              </p>
            </div>
            <div className="problem-card">
              <div className="problem-icon blue">
                <RefreshCw size={22} />
              </div>
              <h3>Maintenance without ownership</h3>
              <p>
                Locator drift, duplicates, and obsolete scenarios pile up. Recommendations must stay human-approved —
                never silent publish.
              </p>
            </div>
            <div className="problem-card">
              <div className="problem-icon red">
                <Users size={22} />
              </div>
              <h3>Release confidence without evidence</h3>
              <p>
                Green bars do not equal ship. Release Confidence should name missing evidence — and never invent ROI or
                production insights you do not have.
              </p>
            </div>
            <div className="problem-card">
              <div className="problem-icon yellow">
                <Lock size={22} />
              </div>
              <h3>Lock-in without an exit</h3>
              <p>
                Quality evidence and runnable exports should leave with you. Portable bundles and Playwright project
                egress are anti-lock-in — not claims of runtime parity.
              </p>
            </div>
            <div className="problem-card">
              <div className="problem-icon blue">
                <Brain size={22} />
              </div>
              <h3>AI that gates the product</h3>
              <p>
                Authoring help is useful. Execution, change intelligence, diagnosis, and release evidence must keep
                working when an LLM provider is unavailable.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section
        className="section"
        id="spine"
        style={{
          background: 'var(--bg-soft)',
          borderTop: '1px solid var(--border)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div className="container">
          <div className="section-header">
            <div className="section-badge">Quality Intelligence</div>
            <h2 className="section-title">One connected change-aware workflow</h2>
            <p className="section-subtitle">
              From commit to confidence — Change Impact, TIA, targeted regression, execution, Failure Intelligence,
              maintenance, Release Confidence, and evidence export.
            </p>
          </div>

          <ol
            className="features-grid"
            style={{ listStyle: 'none', padding: 0, counterReset: 'spine' }}
            aria-label="Quality Intelligence workflow"
          >
            {SPINE.map((step) => (
              <li key={step} className="feature-card" style={{ minHeight: 'auto' }}>
                <h3 style={{ fontSize: '1rem' }}>{step}</h3>
              </li>
            ))}
          </ol>

          <p className="section-subtitle" style={{ marginTop: 24 }}>
            <strong>Governance:</strong> recommendations are not truth. Human approval stays in the loop. Visual
            findings are advisory. Safe-skip and automatic release blocking are not claimed. No invented money, hours,
            or tests-saved metrics.
          </p>
        </div>
      </section>

      <section className="section" id="capabilities">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">Capabilities</div>
            <h2 className="section-title">What change-aware Quality Engineering looks like</h2>
            <p className="section-subtitle">
              Concrete capabilities available in product today — described honestly, without invented scale or customer
              logos.
            </p>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <h3>
                <div
                  className="feature-icon"
                  style={{ background: 'rgba(99, 102, 241, 0.12)', color: 'var(--accent)' }}
                >
                  <GitBranch size={18} />
                </div>
                Change Impact + TIA
              </h3>
              <p>
                Connect a GitLab or Bitbucket repo (or GitHub where configured), compare a commit, and see which
                scenarios and automations the change likely hits — including locator and test-data impact signals.
                Available when a source repo is bound; field accuracy improves with real usage.
              </p>
            </div>
            <div className="feature-card">
              <h3>
                <div className="feature-icon" style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6' }}>
                  <Target size={18} />
                </div>
                Targeted regression
              </h3>
              <p>
                Why These Tests? explains the subset to run for a change. Selection is advisory — Rynora does not claim
                safe-skip authority or invent coverage percentages.
              </p>
            </div>
            <div className="feature-card">
              <h3>
                <div
                  className="feature-icon"
                  style={{ background: 'rgba(34, 197, 94, 0.12)', color: 'var(--success)' }}
                >
                  <Zap size={18} />
                </div>
                Execution + self-healing
              </h3>
              <p>
                Run published flows on SaaS headless or a private execution agent. Self-healing helps recover locators
                during a run; lasting locator changes stay recommendation-led with human approval — not silent publish.
              </p>
            </div>
            <div className="feature-card">
              <h3>
                <div className="feature-icon" style={{ background: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b' }}>
                  <Activity size={18} />
                </div>
                Failure Intelligence
              </h3>
              <p>
                Classify failures, surface similar history, and attach issue-backed resolution evidence where links
                exist. Remediations require human approval. Works without an LLM provider.
              </p>
            </div>
            <div className="feature-card">
              <h3>
                <div className="feature-icon" style={{ background: 'rgba(168, 85, 247, 0.12)', color: '#a855f7' }}>
                  <RefreshCw size={18} />
                </div>
                Maintenance + inventory hygiene
              </h3>
              <p>
                Maintenance recommendations and duplicate / near-duplicate / obsolete / unused detection help clean the
                suite. Findings are compose-on-read heuristics — not automatic archive or skip.
              </p>
            </div>
            <div className="feature-card">
              <h3>
                <div className="feature-icon" style={{ background: 'rgba(14, 165, 233, 0.12)', color: '#0ea5e9' }}>
                  <Server size={18} />
                </div>
                Environment + test data intelligence
              </h3>
              <p>
                Environment bands and test-data impact soft-join into Change Impact and Release Confidence. Soft
                references only — no auto-mutate of data and no release block from missing data alone.
              </p>
            </div>
            <div className="feature-card">
              <h3>
                <div className="feature-icon" style={{ background: 'rgba(236, 72, 153, 0.12)', color: '#ec4899' }}>
                  <Eye size={18} />
                </div>
                Visual regression (advisory)
              </h3>
              <p>
                Visual evidence links into traces and Release Confidence. A visual mismatch is advisory evidence — not
                an automatic product defect and not automatic release blocking.
              </p>
            </div>
            <div className="feature-card">
              <h3>
                <div
                  className="feature-icon"
                  style={{ background: 'rgba(34, 197, 94, 0.12)', color: 'var(--success)' }}
                >
                  <Shield size={18} />
                </div>
                Release Confidence + CI gates
              </h3>
              <p>
                Advisory ship/hold with an explicit missing-evidence list. First-class CI emit for GitHub, GitLab, and
                Jenkins. Azure DevOps push ingestion is available; ADO CI status emit is buyer-gated — not general GA.
              </p>
            </div>
            <div className="feature-card">
              <h3>
                <div
                  className="feature-icon"
                  style={{ background: 'rgba(99, 102, 241, 0.12)', color: 'var(--accent)' }}
                >
                  <FileCheck size={18} />
                </div>
                Evidence bundle + Playwright export
              </h3>
              <p>
                Export a portable quality evidence bundle for audit conversations. Export a published flow as a
                Playwright/TypeScript project for anti-lock-in. Export is not a claim of Rynora runtime parity;
                unsupported steps are labeled clearly.
              </p>
            </div>
            <div className="feature-card">
              <h3>
                <div className="feature-icon" style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6' }}>
                  <Lock size={18} />
                </div>
                Private execution + governance
              </h3>
              <p>
                Run inside your network with a private execution agent when required. SSO, audit, and approval policies
                support regulated teams. Agent fleet scale is available as a capability — not marketed as proven at
                arbitrary concurrency.
              </p>
            </div>
            <div className="feature-card">
              <h3>
                <div className="feature-icon" style={{ background: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b' }}>
                  <Sparkles size={18} />
                </div>
                AI resilience
              </h3>
              <p>
                AI helps author flows and summarize analysis. Deterministic services still run tests, compose Change
                Impact, diagnose failures, and build release evidence when AI credentials are absent or a provider is
                down.
              </p>
            </div>
            <div className="feature-card">
              <h3>
                <div className="feature-icon" style={{ background: 'rgba(168, 85, 247, 0.12)', color: '#a855f7' }}>
                  <Code2 size={18} />
                </div>
                Intent-friendly authoring
              </h3>
              <p>
                Describe journeys in plain language and build visually. Locators still exist under the hood; healing and
                locator-impact evidence reduce maintenance — they do not erase selectors or promise zero upkeep.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="pricing">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">Pricing</div>
            <h2 className="section-title">Transparent. Fair. Published.</h2>
            <p className="section-subtitle">
              No &quot;contact sales&quot; gates for standard plans. Price by value, not by headcount. All plans include
              unlimited users.
            </p>
          </div>

          <div className="pricing-grid">
            <div className="pricing-card">
              <div className="pricing-name">Free</div>
              <div className="pricing-price">
                $0<span> /forever</span>
              </div>
              <div className="pricing-desc">Prove the Quality Intelligence workflow</div>
              <ul className="pricing-features">
                <li>3 projects</li>
                <li>100 test runs/month</li>
                <li>1 parallel worker</li>
                <li>Failure Intelligence + Release Confidence</li>
                <li>Evidence export</li>
                <li className="pricing-feature-disabled">AI authoring credits</li>
              </ul>
              <button className="pricing-btn" onClick={() => onNavigate('register')}>
                Get Started Free
              </button>
            </div>

            <div className="pricing-card">
              <div className="pricing-name">Starter</div>
              <div className="pricing-price">
                $19<span> /month</span>
              </div>
              <div className="pricing-desc">For individuals and small teams</div>
              <ul className="pricing-features">
                <li>10 projects</li>
                <li>500 test runs/month</li>
                <li>200 AI generations/month</li>
                <li>2 parallel workers</li>
                <li>Bring Your Own API Key</li>
                <li>Email support</li>
              </ul>
              <button className="pricing-btn" onClick={() => onNavigate('register')}>
                Start Free Trial
              </button>
              <PaymentMethodsRow />
            </div>

            <div className="pricing-card">
              <div className="pricing-name">Pro</div>
              <div className="pricing-price">
                $39<span> /month</span>
              </div>
              <div className="pricing-desc">Full-featured for growing teams</div>
              <ul className="pricing-features">
                <li>Unlimited projects</li>
                <li>2,000 test runs/month</li>
                <li>500 AI generations/month</li>
                <li>4 parallel workers</li>
                <li>Cross-browser testing</li>
                <li>Self-healing with human approval</li>
                <li>CI/CD quality gates (GH / GL / Jenkins)</li>
              </ul>
              <button className="pricing-btn" onClick={() => onNavigate('register')}>
                Start Free Trial
              </button>
              <PaymentMethodsRow />
            </div>

            <div className="pricing-card featured">
              <div className="pricing-popular">Most Popular</div>
              <div className="pricing-name">Team</div>
              <div className="pricing-price">
                $79<span> /month</span>
              </div>
              <div className="pricing-desc">Scale with change-aware confidence</div>
              <ul className="pricing-features">
                <li>Unlimited projects</li>
                <li>10,000 test runs/month</li>
                <li>5,000 AI generations/month</li>
                <li>8 parallel workers</li>
                <li>Visual regression (advisory evidence)</li>
                <li>Scheduled runs</li>
                <li>Team collaboration</li>
                <li>Priority support</li>
              </ul>
              <button className="pricing-btn primary" onClick={() => onNavigate('register')}>
                Start Free Trial
              </button>
              <PaymentMethodsRow />
            </div>

            <div className="pricing-card">
              <div className="pricing-name">Enterprise</div>
              <div className="pricing-price">Custom</div>
              <div className="pricing-desc">Self-hosted. Compliant. Secure.</div>
              <ul className="pricing-features">
                <li>Everything in Team</li>
                <li>Unlimited AI generations</li>
                <li>Unlimited runs &amp; workers</li>
                <li>Private execution agent</li>
                <li>Self-hosted deployment</li>
                <li>SSO / SAML</li>
                <li>Audit logs &amp; compliance</li>
                <li>Dedicated support engineer</li>
                <li>SLA guarantees</li>
              </ul>
              <Link className="pricing-btn" to="/contact">
                Contact Us
              </Link>
              <PaymentMethodsRow />
            </div>
          </div>

          <div className="pricing-footnote">
            <h3>How AI billing works</h3>
            <div className="pricing-footnote-grid">
              <div className="pricing-footnote-item">
                <Zap size={16} />
                <div>
                  <strong>AI generations</strong>
                  <p>
                    Credits apply to optional authoring and analysis assists. Core execution, Change Impact, Failure
                    Intelligence, and Release Confidence do not require an LLM.
                  </p>
                </div>
              </div>
              <div className="pricing-footnote-item">
                <Shield size={16} />
                <div>
                  <strong>Bring Your Own Key</strong>
                  <p>
                    Use your own provider key on Starter+ for authoring assists — you pay the provider directly. Product
                    spine stays available if the key is missing.
                  </p>
                </div>
              </div>
              <div className="pricing-footnote-item">
                <RefreshCw size={16} />
                <div>
                  <strong>Need more?</strong>
                  <p>Upgrade or downgrade plans as needed. No invented savings claims — price is what you see.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section story-section" id="story">
        <div className="container">
          <div className="story-content">
            <div className="section-badge">Our Story</div>
            <h2>Why we built Quality Intelligence</h2>

            <p>
              Teams were drowning in suites that could not answer a simple release question: given this change, what
              should we run, what failed for real, and what evidence supports shipping?
            </p>

            <p>
              Rewriting frameworks — Selenium to Cypress to Playwright — did not fix the missing spine. Faster script
              generation did not either. The gap is Quality Engineering judgment: change awareness, diagnosis,
              maintenance, and release evidence under governance.
            </p>

            <div className="story-pullquote">
              &quot;Rynora doesn&apos;t just tell you whether a test passed. It connects what changed, what should be
              tested, what failed, what needs attention, and what evidence supports the release decision.&quot;
            </div>

            <p>
              <strong>Rynora is built for that connected story</strong> — governed Quality Intelligence for change-aware
              QE. Intent-friendly authoring remains, but the product category is the quality spine, not another
              automation wrapper.
            </p>

            <p style={{ color: 'var(--text)', fontWeight: 500, marginTop: 32 }}>— Mohan</p>
          </div>
        </div>
      </section>

      <section className="cta-section" id="waitlist">
        <div className="container" style={{ position: 'relative' }}>
          <h2>See change-aware Quality Intelligence on your next release</h2>
          <p>
            Join early access. Bring a real repo when you are ready — we will walk the workflow from Change Impact to
            evidence export.
          </p>

          <form className="waitlist-form" onSubmit={handleWaitlist}>
            <input
              className="waitlist-input"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={submitting}
              autoComplete="email"
            />
            <button className="waitlist-btn" type="submit" disabled={submitting}>
              {submitting ? 'Sending…' : 'Join Waitlist'}
            </button>
          </form>

          {submitted && (
            <div className="waitlist-success">
              You&apos;re on the list! We&apos;ll reach out when early access opens.
            </div>
          )}
          {waitlistError && (
            <div className="waitlist-success" style={{ color: 'var(--err)' }}>
              {waitlistError}
            </div>
          )}
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <BrandMark variant="compact" />
              <p className="footer-tagline">
                Governed Quality Intelligence for change-aware Quality Engineering — the system of record for product
                quality.
              </p>
              <p className="footer-mail">
                Say hi: <a href="mailto:hello@rynora.app">hello@rynora.app</a>
              </p>
            </div>
            <div className="footer-col">
              <h4>Product</h4>
              <ul>
                <li>
                  <a href="#spine">Quality Intelligence</a>
                </li>
                <li>
                  <a href="#capabilities">Capabilities</a>
                </li>
                <li>
                  <a href="#pricing">Pricing</a>
                </li>
                <li>
                  <Link to="/status">Status</Link>
                </li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Resources</h4>
              <ul>
                <li>
                  <Link to="/docs">Documentation</Link>
                </li>
                <li>
                  <Link to="/docs/getting-started">Getting started</Link>
                </li>
                <li>
                  <Link to="/docs/api">API reference</Link>
                </li>
                <li>
                  <Link to="/sovereign-ai">Sovereign AI</Link>
                </li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Company</h4>
              <ul>
                <li>
                  <a href="#story">Story</a>
                </li>
                <li>
                  <Link to="/contact">Contact</Link>
                </li>
                <li>
                  <a href="mailto:hello@rynora.app?subject=Careers">Careers</a>
                </li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Legal</h4>
              <ul>
                <li>
                  <Link to="/trust">Trust Center</Link>
                </li>
                <li>
                  <Link to="/trust/regions">Regions &amp; residency</Link>
                </li>
                <li>
                  <Link to="/privacy">Privacy</Link>
                </li>
                <li>
                  <Link to="/terms">Terms</Link>
                </li>
                <li>
                  <Link to="/cookies">Cookies</Link>
                </li>
                <li>
                  <Link to="/security">Security</Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <span>&copy; 2026 Rynora.</span>
            <span className="footer-bottom-links">
              <a href="https://github.com/rynoratech" target="_blank" rel="noreferrer">
                GitHub
              </a>
              <Link to="/security">security.txt</Link>
            </span>
          </div>
        </div>
      </footer>
    </>
  );
}
